import { BeetsStSWithdrawalAbi } from '../abis/beets'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * Beets stS — paginated user-withdraw enumeration on the SonicStaking
 * proxy. 14-day Sonic validator unbond is the cooldown.
 *
 * Single stage: fetch up to N requests + `withdrawDelay()` constant
 * in one multicall. `WithdrawRequest.isWithdrawn` flips to true on
 * claim.
 *
 * Hardcoded page size of 200 — should cover any realistic user.
 */
export const readerBeets: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []
    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getUserWithdraws',
          params: [user, 0n, 200n, false],
        },
        {
          address: entry.withdrawalContract,
          name: 'withdrawDelay',
          params: [],
        },
      ] as unknown as never[],
      abi: [BeetsStSWithdrawalAbi, BeetsStSWithdrawalAbi] as unknown as never,
    })) as unknown[]
    const reqs = stage1[0] as Array<Record<string, unknown>> | undefined
    const delay = toNumber(stage1[1]) ?? 14 * 86_400
    if (!Array.isArray(reqs)) return []

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < reqs.length; i++) {
      const r = reqs[i]
      const amount = toBigInt(r.assetAmount)
      const ts = toNumber(r.requestTimestamp)
      const isWithdrawn = Boolean(r.isWithdrawn)
      if (amount === undefined || ts === undefined) continue
      const readyAt = ts + delay
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(i),
        amountUnderlying: amount.toString(),
        status: isWithdrawn ? 'claimed' : computeStatus(readyAt),
        readyAt: isWithdrawn ? undefined : readyAt,
      })
    }
    return out
  },
}
